import React from 'react';
import { useState } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import styled, { css } from 'styled-components';
import DOMPurify from 'dompurify';
import { html_beautify } from 'js-beautify';
import { useTranslation } from 'react-i18next';

import type { FieldError } from 'react-hook-form';

import { Fieldset, InputErrorMessage, InputLabel, WYSIWYGEditor } from './components';
import { ButtonClear } from '../Buttons';
import {
  DesignIcon,
  DesignIconActive,
  Preview as PreviewIcon,
  PreviewActive as PreviewActiveIcon,
  SourceIcon,
  SourceIconActive,
} from '@/assets/icons';

interface IWYSIWYGInputProps {
  name: string;
  id?: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  error?: FieldError;
  isTouched?: boolean;
}

const WYSIWYGInput = React.forwardRef<Editor, IWYSIWYGInputProps>(function WYSIWYGInput(
  { onChange, value, ...rest }: IWYSIWYGInputProps,
  ref,
): React.ReactElement {
  const { t } = useTranslation();
  const [editorMode, setEditorMode] = useState<'design' | 'source' | 'preview'>('design');

  return (
    <Fieldset>
      <LabelContainer>
        <InputLabel required={Boolean(rest.required)} htmlFor={rest.id ?? rest.name}>
          {rest.label}
        </InputLabel>
        <ModeSelectContainer>
          <StyledClearButton
            onClick={() => setEditorMode('design')}
            icon={editorMode === 'design' ? DesignIconActive : DesignIcon}
            isActive={editorMode === 'design'}
            type="button"
          >
            {t`resources.modals.editorMode.design`}
          </StyledClearButton>
          <StyledClearButton
            onClick={() => setEditorMode('source')}
            icon={editorMode === 'source' ? SourceIconActive : SourceIcon}
            isActive={editorMode === 'source'}
            type="button"
          >
            {t`resources.modals.editorMode.source`}
          </StyledClearButton>
          <StyledClearButton
            onClick={() => setEditorMode('preview')}
            icon={editorMode === 'preview' ? PreviewActiveIcon : PreviewIcon}
            isActive={editorMode === 'preview'}
            type="button"
          >
            {t`resources.modals.editorMode.preview`}
          </StyledClearButton>
        </ModeSelectContainer>
      </LabelContainer>
      {editorMode === 'design' ? (
        <WYSIWYGEditor onChange={onChange} value={value} ref={ref} />
      ) : editorMode === 'source' ? (
        <DisabledTextarea>{html_beautify(value, { indent_size: 2 })}</DisabledTextarea>
      ) : (
        <Preview dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} />
      )}
      <InputErrorMessage error={rest.error} touched={rest.isTouched} />
    </Fieldset>
  );
});

const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  column-gap: 16px;
`;

const ModeSelectContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  flex: 1;
  justify-content: end;
`;

const StyledClearButton = styled(ButtonClear)<{ isActive: boolean }>`
  font-size: 14px;
  font-weight: 500;

  ${({ isActive }) =>
    isActive &&
    css`
      color: #0087dd;
    `}
`;

const DisabledTextarea = styled.div`
  width: 100%;
  height: 250px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  background-color: #f9fafb;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
`;

const Preview = styled.div`
  width: 100%;
  height: 250px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  overflow: auto;
  padding: 8px;
`;

export { WYSIWYGInput };
